import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import ToolsList from "@/pages/ToolsList";
import ToolPage from "@/pages/ToolPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AuthCallback from "@/pages/AuthCallback";
import Contact from "@/pages/Contact";
import StaticPage from "@/pages/StaticPage";
import Favorites from "@/pages/Favorites";
import NotFound from "@/pages/NotFound";

const STATIC_PAGES = {
  about: {
    titleKey: "about",
    title: "About Us",
    description: "About ImageKitHub — a fast, private, browser-based image toolkit.",
    content: `ImageKitHub is a collection of fast, private image tools that run entirely in your browser. We built it because uploading personal photos to a server just to compress or convert them never felt right.

Our tools use modern browser APIs — Canvas, File System, Web Workers — to give you native-app performance without giving up your privacy. Nothing you upload leaves your device.

We're a small team of engineers and designers based around the world, focused on tools that respect your time, data, and attention.`,
  },
  privacy: { title: "Privacy Policy", description: "How ImageKitHub handles your data.", content: `ImageKitHub is built on a client-side-first architecture. Your images are processed entirely within your browser and are never uploaded to our servers.

We collect anonymous analytics (page views, tool usage counts) to improve the product. We do not collect personal images, personally identifiable information, or sell data to third parties. Newsletter emails are stored only for delivery and can be removed on request.

Third-party services such as Google AdSense may set cookies. Please review their policies for more information.` },
  terms: { title: "Terms and Conditions", description: "Terms of use for ImageKitHub.", content: `By using ImageKitHub, you agree to use our tools for lawful purposes only. Tools are provided "as-is" without warranty of any kind. You retain full ownership of any image you process on the site.

We reserve the right to modify or discontinue any tool at any time without notice. Continued use of the site after changes constitutes acceptance of the new terms.` },
  disclaimer: { title: "Disclaimer", description: "Disclaimer for ImageKitHub content.", content: `The information on ImageKitHub is provided for general purposes only. While we strive for accuracy, we make no warranties about completeness or reliability. Any action you take based on the information here is at your own risk.` },
  cookies: { title: "Cookie Policy", description: "How ImageKitHub uses cookies.", content: `We use minimal, essential cookies to remember preferences such as your dark-mode setting. Third-party services (analytics, AdSense) may set their own cookies. You can disable cookies in your browser at any time.` },
  dmca: { title: "DMCA", description: "DMCA takedown policy.", content: `ImageKitHub respects intellectual property rights. If you believe content on this site infringes your copyright, please email us with the URL, a description of the copyrighted work, and your contact information. We will investigate promptly.` },
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/tools" element={<ToolsList />} />
        <Route path="/tools/:slug" element={<ToolPage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/contact" element={<Contact />} />
        {Object.entries(STATIC_PAGES).map(([slug, data]) => (
          <Route key={slug} path={`/${slug}`} element={<StaticPage {...data} />} />
        ))}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
        <FavoritesProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </AuthProvider>
        </FavoritesProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
